import { EyeOutlined, FileSearchOutlined } from "@ant-design/icons";
import { Badge, Button, Modal, Space, Table, Typography } from "antd";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { openNotification } from "../../components/Notification";
import { UserContext } from "../../contexts/UserContext";
import { encaminhamentoService } from "../../services/resources/encaminhamentoService";
import {
  OPCOES_TAMANHO_PAGINA,
  removeEmpty,
  STATUS_COLORS,
  urls,
  USER_ROLES,
} from "../../utils";

export const ModalEncaminhamentosLegenda = ({
  visible,
  onClose,
  date,
  statusEnums,
  statusLabel,
}) => {
  const history = useHistory();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [encaminhamentos, setEncaminhamentos] = useState([]);
  const [paginacao, setPaginacao] = useState({
    currentPage: 0,
    totalElements: 0,
    size: OPCOES_TAMANHO_PAGINA[0],
  });

  useEffect(() => {
    if (visible && date && statusEnums?.length) {
      fetchEncaminhamentos(0, paginacao.size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, date, statusEnums]);

  const fetchEncaminhamentos = async (page = 0, size = paginacao.size) => {
    setLoading(true);
    try {
      const response = await encaminhamentoService.getAllEncaminhamentos(
        removeEmpty({
          dataPrevistaConclusao: new Date(date),
          codigoInstituicaoDestinataria: user.codigo_instituicao,
          statusList: statusEnums,
          page,
          size,
        })
      );

      setEncaminhamentos(response.data.content || []);
      setPaginacao({
        currentPage: response?.data?.currentPage ?? page,
        totalElements: response?.data?.totalElements ?? 0,
        size: response?.data?.size ?? size,
      });
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao buscar encaminhamentos!</strong>,
        error?.response?.data?.detail
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: <strong>status</strong>,
      dataIndex: "statusDescricao",
      key: "status",
      render: (statusDescricao) => (
        <Badge
          style={{
            backgroundColor: STATUS_COLORS[statusDescricao],
            color: "#333333",
            fontWeight: "bold",
          }}
          count={statusDescricao}
        />
      ),
    },
    {
      title: <strong>protocolo</strong>,
      dataIndex: "denunciaIdentificadorExterno",
      key: "protocolo",
    },
    {
      title: <strong>remetente</strong>,
      dataIndex: "instituicaoRemetenteNome",
      key: "instituicao",
    },
    {
      title: <strong>cidade</strong>,
      dataIndex: "denunciaMunicipio",
      key: "denunciaMunicipio",
    },
    {
      title: <strong>início</strong>,
      dataIndex: "dataInicio",
      key: "dataInicio",
      render: (text) => (text ? moment(text).format("DD/MM/yyyy") : ""),
    },
    {
      title: <strong>prazo</strong>,
      dataIndex: "dataPrevistaConclusao",
      key: "prazo",
      render: (text) =>
        text ? (
          moment(text).isAfter(moment()) ? (
            moment(text).format("DD/MM/yyyy")
          ) : (
            <Typography.Text strong type="danger">
              {moment(text).format("DD/MM/yyyy")}
            </Typography.Text>
          )
        ) : (
          <Typography.Text strong>indefinido</Typography.Text>
        ),
    },
    {
      title: <strong>ações</strong>,
      key: "action",
      width: "1rem",
      render: (record) => {
        const {
          denunciaCodigo,
          denunciaIdentificadorExterno,
          codigo,
          denuncia,
        } = record;
        const isAtendimento = denuncia?.tipoRegistro === "ATENDIMENTO";
        const origem = isAtendimento ? "atendimento" : "denuncia";

        return (
          <Space size="small" style={{ display: "flex", justifyContent: "end" }}>
            {user?.authorities?.includes(
              USER_ROLES.CONSULTAR_ENCAMINHAMENTOS_INSTITUICAO
            ) && (
              <Button
                className="action-button"
                type="primary"
                ghost
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                  history.push({
                    pathname: urls.ENCAMINHAMENTO_DETAILS.replace(
                      ":codigo",
                      codigo
                    ),
                    state: { origem },
                  });
                }}
                title={`Visualizar encaminhamento de ${denunciaIdentificadorExterno}`}
              >
                <EyeOutlined style={{ fontSize: "1.2rem" }} />
              </Button>
            )}
            {user?.authorities?.includes(
              USER_ROLES.CONSULTAR_DETALHE_DENUNCIA
            ) && (
              <Button
                className="action-button"
                type="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                  const targetUrl = isAtendimento
                    ? urls.ATENDIMENTO_DETAILS.replace(
                        ":codigo",
                        denunciaCodigo
                      )
                    : urls.DENUNCIA_DETAILS.replace(":codigo", denunciaCodigo);
                  history.push(targetUrl);
                }}
                title={`Visualizar ${origem} ${denunciaIdentificadorExterno}`}
              >
                <FileSearchOutlined style={{ fontSize: "1.2rem" }} />
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      centered
      title={`Encaminhamentos ${statusLabel} — ${moment(date).format(
        "DD/MM/YYYY"
      )}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
      maskClosable={false}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <Table
        rowKey="codigo"
        loading={loading}
        columns={columns}
        dataSource={encaminhamentos}
        size="small"
        pagination={{
          current: paginacao.currentPage + 1,
          pageSize: paginacao.size,
          total: paginacao.totalElements,
          showSizeChanger: true,
          pageSizeOptions: OPCOES_TAMANHO_PAGINA.map(String),
          onChange: (page, pageSize) =>
            fetchEncaminhamentos(page - 1, pageSize),
        }}
      />
    </Modal>
  );
};
